import m from 'mithril'
import { css } from '@emotion/css'
import {
  colors, font, space, card, btnPrimary, btnSecondary,
  input as inputStyle, label as labelStyle,
} from '../styles/theme.js'
import { admin as adminApi } from '../lib/api.js'
import { adminAuthModel } from '../models/admin-auth.js'

// --- Styles ---

const header = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${space.xl};
`

const pageTitle = css`
  font-size: ${font.size['2xl']};
  font-weight: ${font.weight.bold};
`

const adminEmail = css`
  font-size: ${font.size.sm};
  color: ${colors.textMuted};
  margin-right: ${space.md};
`

const tabs = css`
  display: flex;
  gap: ${space.xs};
  margin-bottom: ${space.xl};
  border-bottom: 1px solid ${colors.border};
`

const tab = css`
  padding: ${space.sm} ${space.md};
  font-size: ${font.size.sm};
  font-weight: ${font.weight.medium};
  color: ${colors.textSecondary};
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  cursor: pointer;
  &:hover { color: ${colors.text}; }
`

const tabActive = css`
  ${tab};
  color: ${colors.brand};
  border-bottom-color: ${colors.brand};
`

const section = css`
  margin-bottom: ${space.xl};
`

const sectionTitle = css`
  font-size: ${font.size.lg};
  font-weight: ${font.weight.semibold};
  margin-bottom: ${space.md};
`

const tableHeader = css`
  display: flex;
  align-items: center;
  gap: ${space.md};
  padding: ${space.sm} ${space.md};
  font-size: ${font.size.xs};
  font-weight: ${font.weight.semibold};
  color: ${colors.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 1px solid ${colors.border};
`

const row = css`
  display: flex;
  align-items: center;
  gap: ${space.md};
  padding: ${space.sm} ${space.md};
  border-bottom: 1px solid ${colors.border};
  &:last-child { border-bottom: none; }
`

const col = (flex) => css`flex: ${flex}; min-width: 0; overflow: hidden; text-overflow: ellipsis;`

const badge = css`
  font-size: ${font.size.xs};
  font-weight: ${font.weight.medium};
  padding: 2px 8px;
  border-radius: 4px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
`

const smallBtn = css`
  font-size: ${font.size.xs};
  color: ${colors.textMuted};
  background: none;
  border: 1px solid ${colors.border};
  border-radius: 4px;
  padding: 3px 8px;
  cursor: pointer;
  &:hover { color: ${colors.brand}; border-color: ${colors.brand}; }
`

const dangerBtn = css`
  ${smallBtn};
  &:hover { color: ${colors.critical}; border-color: ${colors.critical}; }
`

const inlineForm = css`
  display: flex;
  gap: ${space.sm};
  align-items: flex-end;
  margin-top: ${space.md};
`

const errMsg = css`
  font-size: ${font.size.sm};
  color: ${colors.brand};
  margin-top: ${space.xs};
`

const successMsg = css`
  font-size: ${font.size.sm};
  color: ${colors.green};
  margin-top: ${space.xs};
`

const settingRow = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${space.md};
  border-bottom: 1px solid ${colors.border};
  &:last-child { border-bottom: none; }
`

const settingLabel = css`
  font-weight: ${font.weight.medium};
`

const settingDesc = css`
  font-size: ${font.size.sm};
  color: ${colors.textMuted};
  margin-top: ${space.xs};
`

const toggle = css`
  position: relative;
  width: 44px;
  height: 24px;
  background: ${colors.border};
  border-radius: 12px;
  border: none;
  cursor: pointer;
  transition: background 0.2s;
  &::after {
    content: '';
    position: absolute;
    top: 2px;
    left: 2px;
    width: 20px;
    height: 20px;
    background: white;
    border-radius: 50%;
    transition: transform 0.2s;
  }
`

const toggleOn = css`
  ${toggle};
  background: ${colors.brand};
  &::after { transform: translateX(20px); }
`

// --- Users Tab ---

function UsersTab() {
  let users = []
  let loading = true
  let actionError = null

  async function load() {
    loading = true
    try {
      users = await adminApi.getUsers()
    } catch (err) {
      actionError = err.message
    } finally {
      loading = false
      m.redraw()
    }
  }

  async function toggleDisabled(userId, currentVal) {
    actionError = null
    try {
      await adminApi.updateUser(userId, { disabled: !currentVal })
      const user = users.find(u => u.id === userId)
      if (user) user.disabled = currentVal ? 0 : 1
    } catch (err) {
      actionError = err.message
    }
    m.redraw()
  }

  return {
    oninit: load,
    view() {
      if (loading) return m('div', 'Loading users...')

      return m('div', { class: section }, [
        m('h2', { class: sectionTitle }, `Users (${users.length})`),
        actionError ? m('div', { class: errMsg }, actionError) : null,
        m('div', { class: card }, [
          m('div', { key: 'header', class: tableHeader }, [
            m('div', { class: col(3) }, 'Name'),
            m('div', { class: col(3) }, 'Email'),
            m('div', { class: col(1) }, 'Projects'),
            m('div', { class: col(1) }, 'Status'),
            m('div', { class: col(1) }, ''),
          ]),
          ...users.map(user =>
            m('div', { key: user.id, class: row }, [
              m('div', { class: col(3) }, user.name || 'Unnamed'),
              m('div', { class: `${col(3)} ${css`font-size: ${font.size.sm}; color: ${colors.textSecondary};`}` }, user.email),
              m('div', { class: col(1) }, user.project_count || 0),
              m('div', { class: col(1) },
                user.disabled
                  ? m('span', { class: badge, style: `color: ${colors.critical}; background: ${colors.brandLight};` }, 'Disabled')
                  : m('span', { class: badge, style: `color: ${colors.teal}; background: ${colors.teal}15;` }, 'Active'),
              ),
              m('div', { class: col(1) },
                m('button', {
                  class: dangerBtn,
                  onclick: () => toggleDisabled(user.id, user.disabled),
                }, user.disabled ? 'Enable' : 'Disable'),
              ),
            ])
          ),
        ]),
      ])
    },
  }
}

// --- Projects Tab ---

function ProjectsTab() {
  let projects = []
  let loading = true

  async function load() {
    loading = true
    try {
      projects = await adminApi.getProjects()
    } finally {
      loading = false
      m.redraw()
    }
  }

  return {
    oninit: load,
    view() {
      if (loading) return m('div', 'Loading projects...')

      return m('div', { class: section }, [
        m('h2', { class: sectionTitle }, `Projects (${projects.length})`),
        m('div', { class: card }, [
          m('div', { key: 'header', class: tableHeader }, [
            m('div', { class: col(3) }, 'Name'),
            m('div', { class: col(3) }, 'Owner'),
            m('div', { class: col(1) }, 'Members'),
            m('div', { class: col(2) }, 'Created'),
          ]),
          ...projects.map(project =>
            m('div', { key: project.id, class: row }, [
              m('div', { class: col(3) }, project.name),
              m('div', { class: `${col(3)} ${css`font-size: ${font.size.sm}; color: ${colors.textSecondary};`}` },
                `${project.owner_name} (${project.owner_email})`),
              m('div', { class: col(1) }, project.member_count || 0),
              m('div', { class: `${col(2)} ${css`font-size: ${font.size.sm}; color: ${colors.textMuted};`}` },
                new Date(project.created_at).toLocaleDateString()),
            ])
          ),
        ]),
      ])
    },
  }
}

// --- Invites Tab ---

function InvitesTab() {
  let invites = []
  let loading = true
  let inviteEmail = ''
  let creating = false
  let actionError = null
  let actionSuccess = null

  async function load() {
    loading = true
    try {
      invites = await adminApi.getInvites()
    } finally {
      loading = false
      m.redraw()
    }
  }

  async function createInvite() {
    creating = true
    actionError = null
    actionSuccess = null
    try {
      const invite = await adminApi.createInvite({
        email: inviteEmail.trim() || undefined,
      })
      invites.unshift(invite)
      actionSuccess = `Invite created: ${invite.code}`
      inviteEmail = ''
    } catch (err) {
      actionError = err.message
    } finally {
      creating = false
      m.redraw()
    }
  }

  async function revokeInvite(id) {
    actionError = null
    try {
      await adminApi.deleteInvite(id)
      invites = invites.filter(i => i.id !== id)
    } catch (err) {
      actionError = err.message
    }
    m.redraw()
  }

  function inviteStatus(invite) {
    if (invite.used_by) return { label: 'Used', color: colors.textMuted }
    if (new Date(invite.expires_at) < new Date()) return { label: 'Expired', color: colors.critical }
    return { label: 'Active', color: colors.teal }
  }

  return {
    oninit: load,
    view() {
      if (loading) return m('div', 'Loading invites...')

      return m('div', { class: section }, [
        m('h2', { class: sectionTitle }, 'Platform Invites'),

        // Create invite form
        m('div', { class: inlineForm }, [
          m('div', { style: 'flex:1;' }, [
            m('label', { class: labelStyle }, 'Email (optional — leave blank for open invite)'),
            m('input', {
              class: `${inputStyle} ${css`flex:1;`}`,
              type: 'email',
              placeholder: 'user@example.com',
              value: inviteEmail,
              oninput: (e) => { inviteEmail = e.target.value },
            }),
          ]),
          m('button', {
            class: btnPrimary,
            disabled: creating,
            onclick: createInvite,
          }, creating ? 'Creating...' : 'Create invite'),
        ]),

        actionError ? m('div', { class: errMsg }, actionError) : null,
        actionSuccess ? m('div', { class: successMsg }, actionSuccess) : null,

        m('div', { class: `${card} ${css`margin-top: ${space.md};`}` }, [
          m('div', { class: tableHeader }, [
            m('div', { class: col(2) }, 'Code'),
            m('div', { class: col(2) }, 'Email'),
            m('div', { class: col(1) }, 'Status'),
            m('div', { class: col(2) }, 'Expires'),
            m('div', { class: col(1) }, ''),
          ]),
          invites.length === 0
            ? m('div', { class: `${row} ${css`color: ${colors.textMuted}; justify-content: center;`}` }, 'No invites yet')
            : invites.map(invite => {
                const status = inviteStatus(invite)
                return m('div', { key: invite.id, class: row }, [
                  m('div', { class: `${col(2)} ${css`font-family: monospace; font-size: ${font.size.sm};`}` }, invite.code),
                  m('div', { class: `${col(2)} ${css`font-size: ${font.size.sm}; color: ${colors.textSecondary};`}` },
                    invite.email || '(any)'),
                  m('div', { class: col(1) },
                    m('span', { class: badge, style: `color: ${status.color}; background: ${status.color}15;` }, status.label)),
                  m('div', { class: `${col(2)} ${css`font-size: ${font.size.sm}; color: ${colors.textMuted};`}` },
                    new Date(invite.expires_at).toLocaleDateString()),
                  m('div', { class: col(1) },
                    !invite.used_by
                      ? m('button', { class: dangerBtn, onclick: () => revokeInvite(invite.id) }, 'Revoke')
                      : m('span', { class: css`font-size: ${font.size.xs}; color: ${colors.textMuted};` },
                          invite.used_by_name || 'Used')
                  ),
                ])
              }),
        ]),
      ])
    },
  }
}

// --- Settings Tab ---

function SettingsTab() {
  let settings = {}
  let loading = true
  let saving = false

  async function load() {
    loading = true
    try {
      settings = await adminApi.getSettings()
    } finally {
      loading = false
      m.redraw()
    }
  }

  async function toggleSetting(key) {
    saving = true
    const current = settings[key] === 'true'
    try {
      await adminApi.updateSettings({ [key]: String(!current) })
      settings[key] = String(!current)
    } finally {
      saving = false
      m.redraw()
    }
  }

  return {
    oninit: load,
    view() {
      if (loading) return m('div', 'Loading settings...')

      const isOpenReg = settings.open_registration === 'true'

      return m('div', { class: section }, [
        m('h2', { class: sectionTitle }, 'Platform Settings'),
        m('div', { class: card }, [
          m('div', { class: settingRow }, [
            m('div', [
              m('div', { class: settingLabel }, 'Open registration'),
              m('div', { class: settingDesc }, isOpenReg
                ? 'Anyone can create an account'
                : 'New users need an invite code to sign up'),
            ]),
            m('button', {
              class: isOpenReg ? toggleOn : toggle,
              onclick: () => toggleSetting('open_registration'),
              disabled: saving,
            }),
          ]),
        ]),
      ])
    },
  }
}

// --- Main Admin View ---

export function AdminView() {
  let activeTab = 'users'

  const tabComponents = {
    users: UsersTab,
    projects: ProjectsTab,
    invites: InvitesTab,
    settings: SettingsTab,
  }

  return {
    view() {
      return m('div', [
        m('div', { class: header }, [
          m('h1', { class: pageTitle }, 'Admin'),
          m('div', { style: 'display: flex; align-items: center;' }, [
            m('span', { class: adminEmail }, adminAuthModel.admin?.email),
            m('button', {
              class: btnSecondary,
              onclick: () => adminAuthModel.logout(),
            }, 'Log out'),
          ]),
        ]),

        m('div', { class: tabs }, [
          ['users', 'projects', 'invites', 'settings'].map(t =>
            m('button', {
              key: t,
              class: activeTab === t ? tabActive : tab,
              onclick: () => { activeTab = t },
            }, t.charAt(0).toUpperCase() + t.slice(1))
          ),
        ]),

        m(tabComponents[activeTab]),
      ])
    },
  }
}
